import React, { useEffect, useRef, useState } from 'react';
import { IMAGES } from '../data/mock';

/*
  Cinematic full-bleed banner using the production still of
  Bertie Watkins leaning over the interrogation table. Includes
  a subtle parallax effect and a whispered pull-quote.
*/
const Banner = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.05 }
    );
    io.observe(node);

    let raf = 0;
    const onScroll = () => {
      if (!visible) return;
      const rect = node.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // 0 when banner enters bottom, 1 when leaves top
      const progress = 1 - (rect.top + rect.height) / (vh + rect.height);
      setOffset(Math.max(-40, Math.min(40, (progress - 0.5) * 60)));
    };
    const loop = () => {
      onScroll();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [visible]);

  return (
    <section
      ref={ref}
      aria-label="ASH — before the Inquisitor"
      className="relative w-full overflow-hidden"
      style={{ height: 'clamp(420px, 70vh, 780px)' }}
    >
      <div
        role="img"
        aria-label="Bertie Watkins as the Inquisitor at the candlelit interrogation table. ASH — the most immersive Halloween theatre show in London 2026."
        className="absolute inset-0 bg-cover will-change-transform"
        style={{
          backgroundImage: `url(${IMAGES.shadowed})`,
          backgroundPosition: '50% 30%',
          transform: `scale(1.08) translate3d(0, ${offset}px, 0)`,
          transition: 'transform 100ms linear'
        }}
      />
      {/* darkness overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0705] via-[#0a0705]/40 to-[#0a0705]" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 75% 30%, rgba(220,140,60,0.15) 0%, rgba(0,0,0,0) 55%)'
        }}
      />

      <div
        className={`relative z-10 h-full flex flex-col items-center justify-center px-6 text-center transition-all duration-[1400ms] ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <p className="text-[10px] md:text-[11px] tracking-[0.5em] font-fell-sc uppercase text-[#c8935a]">
          Before The Inquisitor
        </p>
        <p
          className="mt-6 font-fell italic text-[#f4e8ce] leading-tight max-w-3xl"
          style={{ fontSize: 'clamp(24px, 3.5vw, 42px)' }}
        >
          &ldquo;Give me your hand. Give me your name. Give me the truth.&rdquo;
        </p>
        <p className="mt-6 text-[10px] tracking-[0.4em] font-fell-sc uppercase text-[#8b7355]">
          — The Inquisitor
        </p>

        <div className="mt-10 h-px w-24 bg-[#c8935a]/50" />
      </div>
    </section>
  );
};

export default Banner;
